import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Scale, Building2, Music, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const areas = [
  {
    number: "01",
    title: "Tributário",
    icon: Scale,
    description:
      "Atuação técnica para garantir que você pague somente o devido, com total respaldo legal. Consultoria voltada à eficiência tributária, prevenção de riscos e recuperação de créditos.",
    services: [
      { name: "Isenção de Imposto de Renda", link: "/servicos/isencao-imposto-renda" },
      { name: "Revisão de Imposto de Renda", link: "/servicos/revisao-imposto-renda" },
      { name: "Revisão de ITBI, IPTU e ISS", link: "/servicos/revisao-itbi-iptu-iss" },
    ],
  },
  {
    number: "02",
    title: "Imobiliário",
    icon: Building2,
    description:
      "A Regularização Imobiliária é essencial para a segurança e valorização do seu patrimônio. Nossa assessoria garante a adequação jurídica e documental do seu imóvel, eliminando riscos e pendências. Conduzimos os processos junto a Cartórios e Prefeituras para que seu bem esteja apto para transações (venda, financiamento) e em total conformidade legal.",
    services: [
      { name: "Regularização Imobiliária", link: "/servicos/regularizacao-imobiliaria" },
    ],
  },
  {
    number: "03",
    title: "Direito Autoral e Propriedade Intelectual",
    icon: Music,
    description:
      "Cuidamos da parte burocrática para que você cuide da sua arte. Organizamos registros, revisamos contratos, protegemos suas obras e ajudamos a evitar dores de cabeça com usos indevidos. Segurança para você focar no estúdio enquanto cuidamos da sua proteção jurídica.",
    services: [
      { name: "Direito Autoral e Propriedade Intelectual", link: "/servicos/direito-autoral" },
    ],
  },
];

export const PracticeAreas = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section id="areas" className="py-12 lg:py-16 bg-secondary/30" ref={containerRef}>
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 lg:mb-24"
        >
          <span className="text-primary text-sm font-medium tracking-widest uppercase mb-4 block">
            Especialidades
          </span>
          <h2 className="font-display text-4xl lg:text-6xl xl:text-7xl font-light text-foreground">
            Áreas de <span className="text-primary">Atuação</span>
          </h2>
        </motion.div>

        {/* Areas Grid - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {areas.map((area, index) => (
            <motion.div
              key={area.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="group relative bg-card border border-border rounded-sm p-8 hover:border-primary/50 transition-all duration-500 card-hover overflow-hidden h-full flex flex-col">
                {/* Background Number */}
                <span className="absolute -right-4 -bottom-8 font-display text-[10rem] font-light text-foreground/[0.03] leading-none pointer-events-none select-none">
                  {area.number}
                </span>

                {/* Content */}
                <div className="relative z-10 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <span className="font-display text-2xl text-primary/50">
                        {area.number}
                      </span>
                      <motion.div
                        whileHover={{ rotate: 10, scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                        className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center"
                      >
                        <area.icon className="w-6 h-6 text-primary" />
                      </motion.div>
                    </div>
                  </div>

                  <h3 className="font-display text-xl lg:text-2xl text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                    {area.title}
                  </h3>

                  <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-1">
                    {area.description}
                  </p>

                  {/* Services */}
                  <div className="space-y-3 mt-auto">
                    {area.services.map((service) => (
                      <Link
                        key={service.name}
                        to={service.link}
                        className="flex items-center justify-between text-sm text-foreground/80 hover:text-primary transition-colors group/link hover:translate-x-1 transition-transform"
                      >
                        <span>{service.name}</span>
                        <span className="flex items-center gap-1 text-primary text-xs font-medium">
                          Saiba Mais
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Hover Line */}
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-primary group-hover:w-full transition-all duration-500" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
